import { HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import * as firebase from 'firebase/app';
import {
  Auth,
  User,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { environment } from 'src/environments/environment';
import { UserData, UserLocalInfo } from '../models/user.model';
import { PosthogService } from '../services/posthog.service';
import { CookiesService } from './cookies.service';
import { logInfoAction } from '../shared/util/error-capture.util';
import { SessionTokenService } from './session-token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public auth: Auth;
  public user: User;
  public userObserver: EventEmitter<any> = new EventEmitter(true);
  parsedToken: any;
  userData: UserLocalInfo;
  private static instance: AuthService;

  static getInstance(): AuthService {
    const instance = AuthService.instance || (window as any)._authServiceInstance;
    if (instance) return instance;
    return null;
  }

  constructor(
    private cookiesService: CookiesService,
    private posthogService: PosthogService,
    private sessionTokenService: SessionTokenService
  ) {
    AuthService.instance = this;
    (window as any)._authServiceInstance = this;
    this.auth = getAuth(
      firebase.initializeApp({
        apiKey: environment.firebaseConfig.apiKey,
        authDomain: environment.firebaseConfig.authDomain,
        projectId: environment.firebaseConfig.projectId,
        storageBucket: environment.firebaseConfig.storageBucket,
        messagingSenderId: environment.firebaseConfig.messagingSenderId,
        appId: environment.firebaseConfig.appId,
      })
    );
    this.auth.languageCode = 'pt-BR';
    this.auth.tenantId = environment.tenantId;
    this.auth.onAuthStateChanged(async (user) => {
      if (user) {
        this.user = user;
        if (!this.userData) {
          this.restoreUserDataFromCookies();
        }
        this.userObserver.emit(this.user);
      } else {
        this.userObserver.emit(false);
      }
    });
  }

  signInWithEmailAndPassword = (email: string, password: string) => {
    return signInWithEmailAndPassword(this.auth, email, password);
  };

  sendPasswordResetEmail = (email: string) => {
    return sendPasswordResetEmail(this.auth, email);
  };

  sendEmailVerification = (user = null) => {
    return sendEmailVerification(user ? user : this.user);
  };

  async signOut() {
    try {
      await this.sessionTokenService.signOut();
    } catch {
      // Local cleanup must happen even if the backend is unavailable.
    } finally {
      this.sessionTokenService.clearTokens();
      this.cookiesService.clearAllCookies();
      this.posthogService.reset();
      this.userData = null;
      logInfoAction('User logged out', {});
      Sentry.setUser(null);
      await this.auth.signOut();
    }
  }

  getUserData(): UserData {
    if (!this.userData) {
      this.restoreUserDataFromCookies();
    }

    const userDataMapped: UserData = {
      uid: this.userData?.cod_usuario || this.cookiesService.getCookie('uid'),
      email: this.userData?.email || this.cookiesService.getCookie('email'),
      role: this.userData?.cod_perfil_membro_saude || this.cookiesService.getCookie('role') || 'PATIENT',
      healthMember: this.userData as any,
      patient: this.userData as any,
    };

    return userDataMapped;
  }

  restoreUserDataFromCookies() {
    const uid = this.cookiesService.getCookie('uid');
    const email = this.cookiesService.getCookie('email');
    const role = this.cookiesService.getCookie('role');
    const userName = this.cookiesService.getCookie('userName');

    if (uid && email) {
      this.userData = {
        cod_usuario: uid,
        email: email,
        cod_perfil_membro_saude: role,
        nom_membro_equipe_saude: role !== 'PATIENT' ? userName : undefined,
        nom_paciente: role === 'PATIENT' ? userName : undefined,
      } as UserLocalInfo;

      Sentry.setUser({
        id: uid,
        email: email,
        username: userName,
      });
    }
  }

  getUserUid(): string {
    const userUid = this.cookiesService.getCookie('uid');
    return userUid ?? this.getUserData().uid;
  }

  getUserRole(): string {
    const userRole = this.cookiesService.getCookie('role');
    return userRole ?? this.userData?.cod_perfil_membro_saude ?? 'notHaveRole';
  }

  getInstitutionId(): string {
    return this.cookiesService.getCookie('institutionId');
  }

  async setUserData(userData: UserLocalInfo) {
    this.userData = userData;
    let isMember = false;
    if (userData?.cod_perfil_membro_saude != null) {
      isMember = true
    }

    Sentry.setUser({
      id: userData.cod_usuario,
      email: userData.email,
      username: isMember ? userData.nom_membro_equipe_saude : userData.nom_paciente,
    });

    await this.setLocalCookies(userData, isMember);

    // Identify user in PostHog
    this.posthogService.identify(userData.cod_usuario, {
      email: userData.email,
      name: isMember ? userData.nom_membro_equipe_saude : userData.nom_paciente,
      role: isMember ? userData.cod_perfil_membro_saude : 'PATIENT',
      institutionName: isMember ? userData.institutionName : undefined,
      isEmailVerified: userData.isEmailVerified
    });
  }

  async setLocalCookies(userData: UserLocalInfo, isMember: boolean) {
    const sessionExpiration = isMember
      ? environment.sessionExpiration
      : environment.accessTokenExpiration;
    this.cookiesService.setCookie('uid', userData.cod_usuario, sessionExpiration);
    this.cookiesService.setCookie('email', userData.email, sessionExpiration);

    if (isMember) {
      this.cookiesService.setCookie('userId', userData.cod_membro_equipe_saude.toString(), sessionExpiration);
      this.cookiesService.setCookie('role', userData.cod_perfil_membro_saude, sessionExpiration);
      this.cookiesService.setCookie('institutionName', userData.institutionName, sessionExpiration);
      this.cookiesService.setCookie('institutionId', userData.cod_unidade_saude.toString(), sessionExpiration);
      this.cookiesService.setCookie('userName', userData.nom_membro_equipe_saude, sessionExpiration);
      this.cookiesService.setCookie('permissions', JSON.stringify(userData.funcionalidades), sessionExpiration);
      return;
    }
    this.cookiesService.setCookie('userId', userData.cod_paciente.toString(), sessionExpiration);
    this.cookiesService.setCookie('role', 'PATIENT', sessionExpiration);
    this.cookiesService.setCookie('isEmailVerified', String(userData.isEmailVerified), sessionExpiration);
    this.cookiesService.setCookie('userName', userData.nom_paciente, sessionExpiration);
  }

  async haveSetToken() {
    try {
      await this.sessionTokenService.ensureValidAccessToken();
      return true;
    } catch {
      return false;
    }
  }

  getValidAccessToken(): Promise<string> {
    return this.sessionTokenService.ensureValidAccessToken();
  }

  setAccessToken(accessToken: string): void {
    this.sessionTokenService.setAccessToken(accessToken);
  }

  clearTokenSession(): void {
    this.sessionTokenService.clearTokens();
  }

  refreshAccessToken(forceRefresh = true): Promise<string> {
    return this.sessionTokenService.ensureValidAccessToken(forceRefresh);
  }

  isEmailVerified(): boolean {
    const isVerified = this.cookiesService.getCookie('isEmailVerified');
    return isVerified == 'true' ? true : false;
  }

  checkUserRole(expectedRole: string): boolean {
    const userRole = this.getUserRole();
    return userRole === expectedRole;
  }

  async buildAPIAuthHeader(contentType = null): Promise<HttpHeaders> {
    const accessToken = await this.sessionTokenService.ensureValidAccessToken();
    if (contentType) {
      return new HttpHeaders().append('authorization', `Bearer ${accessToken}`);
    }
    return new HttpHeaders()
      .append('authorization', `Bearer ${accessToken}`)
      .append('Content-Type', 'application/json');
  }

  async buildAPIAuthHeaderMultiPart(contentType = null): Promise<HttpHeaders> {
    const accessToken = await this.sessionTokenService.ensureValidAccessToken();
    if (contentType) {
      return new HttpHeaders().append('authorization', `Bearer ${accessToken}`);
    }
    return new HttpHeaders().append('authorization', `Bearer ${accessToken}`);
  }
}

export const ROLES = {
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER',
  PATIENT: 'PATIENT',
  IGESTO_ADMIN: 'IGESTO_ADMIN',
};
